/**
 * Bearer-token GET against the backend (Firebase ID token), unwrapping the
 * `{ error }` body on failure. Shared by the compliance + reporting APIs.
 */

import { auth } from '../firebase';

/**
 * Headers carrying the signed-in user's ID token, for callers that keep their
 * own response handling (e.g. 409 bodies). Throws when nobody is signed in.
 */
export async function bearerHeaders(extra = {}) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');
  return { ...extra, Authorization: `Bearer ${idToken}` };
}

export async function authorizedFetch(url, failureMessage, { signal } = {}) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });

  if (!response.ok) {
    let message = failureMessage;
    let code;
    try {
      const data = await response.json();
      message = data.error || message;
      code = data.code;
    } catch {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    if (code) error.code = code;
    throw error;
  }
  return response.json();
}

/**
 * Method-aware sibling of authorizedFetch for JSON writes. Same error
 * unwrapping; `error.status` carries the HTTP status.
 */
export async function authorizedRequest(url, { method = 'GET', body, signal } = {}, failureMessage) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const response = await fetch(url, {
    method,
    signal,
    headers: {
      Authorization: `Bearer ${idToken}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let message = failureMessage;
    let code;
    let missing;
    try {
      const data = await response.json();
      message = data.error || message;
      code = data.code;
      // What a refused document still needs on the client's file (Para, Stage 7).
      if (Array.isArray(data.missing)) missing = data.missing;
    } catch {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    if (code) error.code = code;
    if (missing) error.missing = missing;
    throw error;
  }
  return response.json();
}

/** The file name in a Content-Disposition header (the UTF-8 `filename*` first), or null. */
export function fileNameFromDisposition(header) {
  const value = String(header || '');
  const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      // fall through to the plain name
    }
  }
  const plain = value.match(/filename="([^"]+)"/i);
  return plain ? plain[1] : null;
}

/**
 * Bearer-token GET for a file download. Resolves to `{ blob, fileName }`;
 * a response that is not `expectType` (Hosting answering with index.html)
 * is an error, as is a `{ error }` body.
 */
export async function authorizedFetchFile(url, failureMessage, { expectType, signal } = {}) {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${idToken}` },
    signal,
  });

  if (!response.ok) {
    let message = failureMessage;
    try {
      const data = await response.json();
      message = data.error || message;
    } catch {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  const type = response.headers.get('content-type') || '';
  if (expectType && !type.includes(expectType)) throw new Error(failureMessage);
  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get('content-disposition')),
  };
}

/** `?a=1&b=2` from an object, dropping null / undefined / '' values. */
export function buildQueryString(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}
